import React, {
  useState, useEffect, useRef,
} from 'react';
import Head from 'next/head';
import {useRouter} from 'next/router';
import _ from 'lodash';

import Link from '../../components/Link';
import CoachPackageCard from '../../components/CoachPackageCard';
import CoachCard from '../../components/CoachCard';
import CourseCard from '../../components/CourseCard';
import Button, {ButtonTypes} from '../../components/ButtonNew';
import DocumentHeader from '../../components/DocumentHeader';
import CoachingFilters from './CoachingFilters';
import CoachLandingHero from './CoachLandingHero';
import FAQSection from '../../components/FAQSection';
import {useAnalytics, useAppContext} from '../../hooks';
import {BASE_URL} from '../../constants/config';
import {airtableTagToDbTag} from '../../../../lib/utils';
import {CompanyExperience, getCompanyExperience} from '../../../../lib/utils/coaching';
import {airtableRoleToRoleMap} from '../../../../lib/constants/airtableTags';
import {
  CoachingFAQs, CoachingService, AirtableServiceNames,
} from '../../constants/coaching';
import {cx} from '../../util';
import useSidebarNavEnabled from '../../components/navigation/useSidebarNav';
import {getCanonicalURL} from '../../util/seo';
import {StarIcon} from '../../components/svg';
import {startPreCheckout} from '../PreCheckoutPage';

const Placeholder = () => (
  <div className="flex flex-col h-full text-left p-6 pb-5 shadow rounded border border-gray-200 animate-pulse">
    <div>
      <span className="block h-12 w-12 bg-gray-100 rounded" />
      <span className="block h-8 bg-gray-100 mt-4 mb-3 w-2/3 rounded" />
    </div>
    <div className="flex flex-col justify-between h-full">
      <span className="h-4 w-full bg-gray-100 rounded mb-2" />
      <span className="h-4 w-3/4 bg-gray-100 rounded mb-2" />
      <span className="h-4 w-3/4 bg-gray-100 rounded mb-4" />
    </div>
  </div>
);

const filterCoaches = (coaches = [], {
  category, company, role, skill, industry,
}) => coaches.filter((coach) => {
  const services = coach.Services?.map((s) => AirtableServiceNames[s]) || [];
  const roles = _.compact(_.map(coach.Role, (r) => airtableRoleToRoleMap[r])) || [];
  const skills = coach.Skills || [];
  const industries = coach.Industries || [];

  if (category && !services.includes(category)) return false;

  if (category !== CoachingService.NEGOTIATION) {
    if (company && !getCompanyExperience(coach, company)) return false;
    if (role && !(roles.includes(role))) return false;
    if (skill && !(skills.includes(skill))) return false;
    if (industry && !(industries.includes(industry))) return false;
  }

  return true;
});

// Coaches who worked at the filtered company lead; the rest follow under
// "More relevant coaches".
const hasDirectExperience = (coach, {category, company}) => (
  !company
  || category === CoachingService.NEGOTIATION
  || getCompanyExperience(coach, company) === CompanyExperience.WORKED
);

// Lead the card's logo pile with the filtered company for coaches who worked
// there; their legacy Companies list often leaves it out
const getCardCompanies = (coach, company) => {
  if (getCompanyExperience(coach, company) !== CompanyExperience.WORKED) {
    return coach.Companies;
  }
  const worked = _.find(coach['Companies Worked'], (c) => airtableTagToDbTag(c) === company);
  return [worked, ...(coach.Companies || [])]; // CoachCard dedupes by tag
};

const NegotiationTestimonial = () => (
  <section className="bg-gray-100 mb-12 -mx-[var(--page-gutter,1.5rem)]">
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 md:flex md:flex-col md:py-10">
      <div className="flex items-center justify-center mx-auto mb-6">
        <StarIcon className="h-6 w-6 text-yellow-400" />
        <StarIcon className="h-6 w-6 text-yellow-400" />
        <StarIcon className="h-6 w-6 text-yellow-400" />
        <StarIcon className="h-6 w-6 text-yellow-400" />
        <StarIcon className="h-6 w-6 text-yellow-400" />
        <span className="hidden sm:inline ml-2 text-gray-500 font-semibold">99% success rate for hundreds of offers</span>
      </div>
      <div className="relative text-2xl font-medium text-gray-900 md:grow">
        <p className="relative text-center">
          &ldquo;My negotiation coach was truly an expert. I negotiated a $50k increase in my salary
          and I couldn&apos;t have done it without Aced!&rdquo;
        </p>
      </div>
      <div className="mt-4 flex justify-center items-center">
        <div className="flex flex-col sm:flex-row">
          <div className="text-base font-medium text-gray-500 ml-4 sm:ml-2">— Sonia, Senior Product Manager</div>
        </div>
      </div>
    </div>
  </section>
);

const PageHeaders = {
  default: {
    title: 'Coaching and Mock Interviews for Your Tech Job',
    header: 'Expert coaching, mock interviews, and more',
    subheader: 'Connect with an expert in your field to practice mock interviews, review your resume, or create a study plan.',
    showTestimonial: true,
  },
  [CoachingService.MOCK_INTERVIEW]: {
    title: 'Coaching and Mock Interviews for Your Tech Job',
    header: 'Expert coaching, mock interviews, and more',
    subheader: 'Connect with an expert in your field to practice mock interviews, review your resume, or create a study plan.',
    showTestimonial: true,
  },
  [CoachingService.RESUME_REVIEW]: {
    title: 'Coaching and Mock Interviews for Your Tech Job',
    header: 'Get a resume review from a tech hiring manager',
    subheader: 'Your resume is your first impression. Get more offers with a professional review today.',
    showTestimonial: true,
  },
  [CoachingService.NEGOTIATION]: {
    title: 'Tech Salary Negotiation Coaching',
    header: 'Maximize your tech salary and compensation.',
    subheader: 'Don’t leave money on the table—greatly increase your offers with professional salary negotiation help. Money-back guarantee.',
    showTestimonial: false,
  },
  [CoachingService.CAREER_COACHING]: {
    title: 'Coaching and Mock Interviews for Your Tech Job',
    header: 'Level up your career with help from an expert mentor',
    subheader: 'Learn anything you need to know from our team of tech professionals.',
    showTestimonial: true,
  },
  [CoachingService.COMMUNICATION]: {
    title: 'Communication Coaching for Your Tech Job',
    header: 'Level up your career with help from an expert mentor',
    subheader: 'Learn anything you need to know from our team of tech professionals.',
    showTestimonial: false,
  },
};

const CoachingPage = ({
  coaches,
  tags,
  tagMap,
  recommendedCoaches = [],
  query: initialQuery,
  course,
  courseForRole,
  rolePathExists,
}) => {
  const {
    features,
    onCheckout,
    catalog,
    currentUser,
  } = useAppContext();

  const router = useRouter();
  const {tracker} = useAnalytics();
  const sidebarShell = useSidebarNavEnabled();
  const { // Use `initialQuery` for SSR on first load; switch to `router.query` after hydration
    role: roleParam, company, category, skill, industry,
  } = router.isReady ? router.query : initialQuery;
  // Repeated ?role= params arrive as arrays; use the first
  const role = Array.isArray(roleParam) ? roleParam[0] : roleParam;
  const filterKey = [company, role, category, skill, industry].join('|');
  const [coachGroups, setCoachGroups] = useState({direct: [], related: [], filterKey: null});
  const coachesEl = useRef();
  const reportedNoResultsKey = useRef(null);

  const {direct: directCoaches, related: relatedCoaches} = coachGroups;
  const filteredCoaches = React.useMemo(
    () => [...directCoaches, ...relatedCoaches],
    [directCoaches, relatedCoaches],
  );
  const formattedRole = (role && _.find(tags?.role, ['id', role])?.name) || '';
  const formattedCompany = (company && _.find(tags?.company, ['id', company])?.name) || '';

  const packageAvatars = React.useMemo(() => (
    _.chain(coaches || [])
      .filter((c) => c.Photo)
      .sortBy('slug')
      .slice(0, 3)
      .map((c) => c.Photo)
      .value()
  ), [coaches]);

  useEffect(() => {
    let filtered = filterCoaches(coaches, {
      category,
      company,
      role,
      skill,
      industry,
    });

    if (recommendedCoaches?.length > 0) {
      filtered = _.sortBy(filtered, [
        (coach) => !recommendedCoaches.includes(coach.slug), // Recommended coaches first
        (coach) => ((
          coach.Services?.includes('Resume Review')
          || coach.Services?.includes('Resume Review Call')
        ) ? 1 : 0), // Push these services to the end
      ]);
    }

    const [direct, related] = _.partition(
      filtered,
      (coach) => hasDirectExperience(coach, {category, company}),
    );
    setCoachGroups({direct, related, filterKey});
  }, [coaches, company, role, category, skill, industry, filterKey, recommendedCoaches]);

  useEffect(() => {
    if (!router.isReady) return;
    tracker.coachLandingView({
      company, role, category, skill, industry,
    });
  }, [company, role, category, skill, industry, router, tracker]);

  // Empty results are invisible in the funnel otherwise. Wait until the groups
  // were computed for the current filters so a stale empty list doesn't fire,
  // and report each empty filter set once (the tracker is rebuilt on rerenders).
  useEffect(() => {
    if (!router.isReady || coachGroups.filterKey !== filterKey) return;
    if (filteredCoaches.length > 0 || !(company || role || category || skill || industry)) {
      reportedNoResultsKey.current = null;
      return;
    }
    if (reportedNoResultsKey.current === filterKey) return;
    reportedNoResultsKey.current = filterKey;
    tracker.coachLandingNoResults({
      company, role, category, skill, industry,
    });
  }, [
    coachGroups.filterKey, filterKey, filteredCoaches,
    company, role, category, skill, industry, router, tracker,
  ]);

  const showPackage = (
    features?.coaching_package === 'on'
    && category !== CoachingService.NEGOTIATION
  );

  const showNegotiationPackage = (
    features?.coaching_package === 'on'
    && category === CoachingService.NEGOTIATION
  );
  const guidedCoachingFlowEnabled = features?.guided_coaching_flow === 'on';

  const startGuidedCoachingFlow = (source) => {
    tracker.coachingLandingClickCta({
      cta: 'guided_coaching_flow',
      source,
      category,
      role,
      company,
      skill,
      industry,
      hasUser: !!currentUser,
      hasCareerTracks: !!currentUser?.profile?.career_tracks?.length,
    });
    router.push('/coaching/search');
  };

  const onClickPackage = (quantity = 1) => {
    tracker.coachClickPackage({quantity});
    const cancelUrl = `${BASE_URL}/coaching?payment_canceled=true`;
    const returnPath = '/coaching';
    let products = [{id: catalog.COACHING_SESSION.name, quantity}];
    if (quantity === 5) {
      products = [{id: catalog.COACHING_SESSION_5_PACK.name, quantity}];
    } else if (quantity === 3) {
      products = [{id: catalog.COACHING_SESSION_3_PACK.name, quantity}];
    }

    if (features?.enable_pre_checkout === 'on') {
      startPreCheckout(router, {cart: products, returnPath}, tracker);
    } else {
      onCheckout(products, currentUser, {cancelUrl});
    }
  };

  const chooseCoach = () => {
    let selectedCoach;
    const interviewCompanies = currentUser?.profile?.interview_companies;
    // Match based on user's interested companies
    if (interviewCompanies) {
      selectedCoach = filteredCoaches.find((coach) => {
        const companies = coach.Companies?.map(airtableTagToDbTag) || [];
        const intersection = _.intersection(companies, interviewCompanies);
        return intersection.length > 0;
      });
    }

    // Match based on number of positive feedback
    if (!selectedCoach) {
      selectedCoach = _.maxBy(filteredCoaches, (coach) => coach.numHighlights);
    }

    router.push(`/coach/${selectedCoach.slug}`);
  };

  const onClickCoach = (coach) => (e) => {
    e.stopPropagation();
    tracker.coachClickProfile({
      coach: coach.slug,
      recommended: recommendedCoaches.includes(coach.slug),
      experience: company ? getCompanyExperience(coach, company) : null,
      category,
      company,
      role,
    });
    router.push(`/coach/${coach.slug}`);
  };

  const scrollToCoaches = () => coachesEl?.current.scrollIntoView({behavior: 'smooth'});

  const onHeroCTA = guidedCoachingFlowEnabled
    ? () => startGuidedCoachingFlow('hero')
    : scrollToCoaches;

  // Update URL query when filters change
  const onFilter = (param, value) => {
    const query = {...router.query};
    if (!value) {
      delete query[param];
    } else {
      query[param] = value;
    }
    router.push({
      pathname: router.pathname,
      query,
    }, null, {shallow: true});
  };

  const onClearFilters = () => {
    const query = _.without(router.query, ['role', 'company']);
    router.push({
      pathname: router.pathname,
      query,
    }, null, {shallow: true});
  };

  let {
    title, header, subheader,
  } = PageHeaders[category] || PageHeaders.default;

  const {showTestimonial} = PageHeaders[category] || PageHeaders.default;

  const FAQs = category ? CoachingFAQs.filter((FAQ) => FAQ.category.includes(category))
    : CoachingFAQs;

  const renderCoachCard = (coach) => (
    <CoachCard
      key={coach.slug}
      name={coach.Name}
      title={coach.Title}
      image={coach.Photo}
      href={`/coach/${coach.slug}`}
      companies={company ? getCardCompanies(coach, company) : coach.Companies}
      skills={coach.Skills}
      services={coach.Services}
      rating={coach.Rating}
      description={coach['Brief Bio']}
      numSessions={coach['Total number of sessions completed']}
      recommended={recommendedCoaches.includes(coach.slug)}
      onClick={onClickCoach(coach)}
      tagMap={tagMap}
    />
  );

  if (tags) {
    if (role || company) {
      title = `${formattedCompany ? `${formattedCompany} ` : ''}${formattedRole ? `${formattedRole} ` : ''}Interview Coaching and Mock Interviews`;
      subheader = `Practice mock interviews, review your resume, or get 1-1 career coaching with an expert ${formattedCompany ? `${formattedCompany} ` : ''}${formattedRole ? `${formattedRole.toLowerCase()} ` : ''}coach.`;
      header = `Get expert coaching for ${formattedCompany ? `${formattedCompany} ` : ''}${formattedRole ? `${formattedRole.toLowerCase()} ` : ''}interviews`;
    }
  }

  return (
    // Full-bleed children cancel --page-gutter; clip (not hidden) keeps sticky working
    <div className={cx(
      'bg-white px-[var(--page-gutter)] [--page-gutter:1.5rem] overflow-x-clip',
      sidebarShell && 'lg:[--page-gutter:2.5rem]',
    )}
    >
      <DocumentHeader
        title={title}
        metaDescription={subheader}
        canonicalUrl={getCanonicalURL(router.asPath, ['role', 'company'])}
        prerender404={filteredCoaches.length === 0}
      />
      {/* Canonical for role-filter variants that still render (bare ones 308
          server-side); same head key as DocumentHeader's so only one wins */}
      {rolePathExists && role && !company && !category && !skill && !industry && (
        <Head>
          <link rel="canonical" href={getCanonicalURL(`/coaching/${role}`)} key="canonical" />
        </Head>
      )}

      <CoachLandingHero
        pageTitle={header}
        pageSubtitle={subheader}
        onClickCTA={onHeroCTA}
        showTestimonial={showTestimonial}
        compactTitle={!!role && !!company}
      />
      {category === CoachingService.NEGOTIATION && (<NegotiationTestimonial />)}
      <div className="mx-auto mb-12 flex flex-col max-w-7xl" ref={coachesEl}>
        <span className="mb-4 text-2xl font-bold">Browse all coaches</span>
        {!coaches ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {_.times(6, (i) => <Placeholder key={i} />)}
          </div>
        ) : (
          <>
            <CoachingFilters
              role={role}
              company={company}
              category={category}
              skill={skill}
              industry={industry}
              onChange={onFilter}
              tags={tags}
            />
            {filteredCoaches.length === 0 && (
              <div className="flex flex-col w-full text-center text-base text-gray-500 p-10 border-3 border-dashed border-gray-300 rounded-lg">
                Sorry, we couldn&rsquo;t find any coaches that match those filters.
                <Button buttonType={ButtonTypes.TERTIARY} onClick={onClearFilters}>
                  Reset search
                </Button>
              </div>
            )}
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {showPackage && filteredCoaches.length !== 0 && (
                <CoachPackageCard
                  name={guidedCoachingFlowEnabled
                    ? 'Find the right coaching package'
                    : 'Save with our discounted 5-session package'}
                  description={guidedCoachingFlowEnabled
                    ? 'Answer a few quick questions and we\u2019ll recommend the right coach, session type, and bundle for exactly what you\u2019re working toward.'
                    : 'Buy sessions now and get matched with the right coach after purchase. Use at any time and schedule at your convenience.'}
                  onClick={guidedCoachingFlowEnabled
                    ? () => startGuidedCoachingFlow('package_card')
                    : () => onClickPackage(5)}
                  buttonText={guidedCoachingFlowEnabled ? 'Find my coach' : 'Book now'}
                  variant={guidedCoachingFlowEnabled ? 'guided' : 'default'}
                  avatars={packageAvatars}
                />
              )}
              {showNegotiationPackage && filteredCoaches.length !== 0 && (
                <CoachPackageCard
                  name="Not sure? Let us pick the best negotiator for you."
                  description="We'll match you with an expert negotiator involving as many back-and-forth emails and calls as you need to increase your offer. Or your money back."
                  onClick={() => chooseCoach()}
                />
              )}
              {directCoaches.map(renderCoachCard)}
            </div>
            {relatedCoaches.length > 0 && (
              // Hairline divider, skipped when nothing sits above it
              <div className={cx(
                'flex flex-col',
                (directCoaches.length > 0 || showPackage) && 'mt-10 pt-8 border-t border-gray-200',
              )}
              >
                <h2 className="text-base font-semibold text-gray-900">More relevant coaches</h2>
                <p className="mt-1 text-sm text-gray-500">
                  {`Coaches who've interviewed at ${formattedCompany || 'this company'} or helped candidates prepare for interviews there.`}
                </p>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedCoaches.map(renderCoachCard)}
                </div>
              </div>
            )}
          </>
        )}
        {/* Hidden if the user shallow-routes away from the SSR'd role */}
        {course && courseForRole && role === courseForRole && (
          <div className="mt-16 flex flex-col w-full">
            <span className="mb-1 text-2xl font-bold">Prep on your own time</span>
            <p className="mb-4 text-gray-500 font-medium">
              Study with our self-paced course, then book a coach to practice live.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              <CourseCard
                course={course}
                useFullTitle
                trackerEvent={() => tracker.coachingClickCourse({role, course: course.slug})}
                urlParams={{src: 'coaching-role'}}
              />
            </div>
          </div>
        )}
        <div className="mt-16 flex flex-col w-full items-center">
          <div className="w-full flex flex-col max-w-3xl">
            <h2 className="text-xl md:text-2xl font-extrabold text-black">Frequently asked questions</h2>
            <FAQSection items={FAQs} />
          </div>
          <div className="py-10 flex flex-col text-center">
            <h2 className="mb-2 text-xl md:text-2xl font-extrabold text-black">Explore coaching by roles</h2>
            <ul>
              {tags?.role.map((tag) => (
                <li key={tag.id}>
                  <Link className="text-sm md:text-base text-indigo-600 font-medium" href={`/coaching/${tag.id}`}>
                    {tag.name}
                    {' '}
                    Coaching
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {/* Internal links so the /coaching/{skill} pages aren't orphaned */}
          {tags?.skill?.length > 0 && (
            <div className="pb-10 flex flex-col text-center">
              <h2 className="mb-2 text-xl md:text-2xl font-extrabold text-black">Explore coaching by skill</h2>
              <ul className="max-w-3xl">
                {tags.skill.map((tag) => (
                  <li key={tag.id} className="inline-block mx-2">
                    <Link className="text-sm md:text-base text-indigo-600 font-medium" href={`/coaching/${tag.id}`}>
                      {tag.name}
                      {' '}
                      Coaching
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {tags?.industry?.length > 0 && (
            <div className="pb-10 flex flex-col text-center">
              <h2 className="mb-2 text-xl md:text-2xl font-extrabold text-black">Explore coaching by industry</h2>
              <ul className="max-w-3xl">
                {tags.industry.map((tag) => (
                  <li key={tag.id} className="inline-block mx-2">
                    <Link className="text-sm md:text-base text-indigo-600 font-medium" href={`/coaching/${tag.id}`}>
                      {tag.name}
                      {' '}
                      Coaching
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachingPage;
